import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Flex, Text, Button, TextField, Box, IconButton, Separator, Tabs, Select, Tooltip, Dialog, Progress, AlertDialog, Badge } from "@radix-ui/themes";
import { ArrowLeftIcon, EnvelopeOpenIcon, CopyIcon } from "@radix-ui/react-icons";
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/16/solid";
import { useProfileContext } from "context";
import { useMarketingEmailTemplates, useMarketingSenderDomains } from "hooks";
import { useToast } from '../../context/ToastContext';
import { ToastAlert } from "components";
import { sendTestEmail } from '../../api/bMarketingApi';

// Import modular templates
import ElegantClassicTemplate from './templates/ElegantClassicEmailTemplate';
import ThankYouReviewTemplate from './templates/ThankYouReviewTemplate';
import HospitalityPromoTemplate from './templates/HospitalityPromoTemplate';

// Utility function to validate file size (1MB max)
const validateFileSize = (file) => {
  const maxSizeInBytes = 1048576; // 1MB
  if (file && file.size > maxSizeInBytes) {
    return false;
  }
  return true;
};

// Template mapping by ID
const TEMPLATES = {
  'elegant-classic': ElegantClassicTemplate,
  'thank-you-review': ThankYouReviewTemplate,
  'hospitality-promo': HospitalityPromoTemplate,
};

// Purpose to template mapping (default template for each purpose)
const PURPOSE_TEMPLATE_MAP = {
  welcome: 'elegant-classic',
  review: 'thank-you-review',
  promotion: 'hospitality-promo',
  newsletter: 'elegant-classic',
};

// Email purpose metadata
const EMAIL_PURPOSES = {
  welcome: {
    name: "Welcome Email",
    description: "Sent immediately when a guest connects to WiFi",
    defaultSubject: "Welcome to {{business_name}}!",
  },
  review: {
    name: "Review Request",
    description: "Sent after guest visit to request a review",
    defaultSubject: "How was your visit to {{business_name}}?",
  },
  promotion: {
    name: "Promotional Email",
    description: "Marketing campaigns and special offers",
    defaultSubject: "Special offer from {{business_name}}",
  },
};

export default function EmailTemplateEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();

  const [view, setView] = useState("desktop");
  
  // Test email dialog state
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailError, setTestEmailError] = useState("");
  const [testSenderDomainId, setTestSenderDomainId] = useState("default");
  
  // Discard changes alert dialog state
  const [discardAlertOpen, setDiscardAlertOpen] = useState(false);
  
  // Back navigation alert dialog state
  const [backAlertOpen, setBackAlertOpen] = useState(false);
  
  // Duplicate template dialog state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateForm, setDuplicateForm] = useState({ name: "", subject: "", purpose: "welcome" });
  
  const isNewTemplate = templateId === 'new';
  const templateData = location.state?.template;
  
  const { emailTemplates, updateTemplate, createTemplate, isUpdating, isCreating, duplicateTemplateAsync, isDuplicating } = 
    useMarketingEmailTemplates(userProfile?.organisation_id);
  
  const { senderDomains } = useMarketingSenderDomains(userProfile?.organisation_id);
  
  // Loading state - show loader until we've initialized the template
  const [isInitializing, setIsInitializing] = useState(!isNewTemplate);
  
  // Find existing template if editing
  const existingTemplate = !isNewTemplate && emailTemplates?.find(
    t => t.template_id === templateId
  );

  // Determine which visual template to use
  const [selectedTemplateId, setSelectedTemplateId] = useState('elegant-classic');
  
  // Get the active template module
  const activeTemplate = useMemo(() => {
    return TEMPLATES[selectedTemplateId] || ElegantClassicTemplate;
  }, [selectedTemplateId]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    purpose: "welcome",
    subject: "",
    templateId: "elegant-classic",
    ...ElegantClassicTemplate.getDefaultValues('welcome'),
  });
  
  // Store original form data for comparison
  const [originalFormData, setOriginalFormData] = useState(null);
  
  const [activeTab, setActiveTab] = useState("general");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Image upload state
  const [logoUpload, setLogoUpload] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const logoInputRef = useRef(null);
  const [deleteLogo, setDeleteLogo] = useState(false);

  const [backgroundImageUpload, setBackgroundImageUpload] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [backgroundFileName, setBackgroundFileName] = useState("");
  const backgroundInputRef = useRef(null);
  const [deleteBackgroundImage, setDeleteBackgroundImage] = useState(false);

  // Handle purpose change
  const handlePurposeChange = (newPurpose) => {
    const defaultTemplateId = PURPOSE_TEMPLATE_MAP[newPurpose];
    const template = TEMPLATES[defaultTemplateId];
    
    setSelectedTemplateId(defaultTemplateId);
    setFormData(prev => ({
      ...prev,
      purpose: newPurpose,
      subject: EMAIL_PURPOSES[newPurpose].defaultSubject,
      templateId: defaultTemplateId,
      ...template.getDefaultValues(newPurpose),
    }));
  };

  // Check if template is imported (non-editable)
  const isImportedTemplate = existingTemplate?.template_source && 
                             existingTemplate.template_source !== 'internal' && 
                             existingTemplate.is_editable === false;
  
  // Initialize form from existing template or template data
  useEffect(() => {
    if (existingTemplate) {
      const designJson = existingTemplate.design_json || {};
      const savedTemplateId = designJson.templateId || PURPOSE_TEMPLATE_MAP[existingTemplate.purpose] || 'elegant-classic';
      const template = TEMPLATES[savedTemplateId] || ElegantClassicTemplate;
      
      setSelectedTemplateId(savedTemplateId);
      const initialData = {
        name: existingTemplate.name || "",
        purpose: existingTemplate.purpose || "welcome",
        subject: existingTemplate.subject || "",
        templateId: savedTemplateId,
        ...template.getDefaultValues(existingTemplate.purpose),
        ...designJson, // Override with saved values
        // Use S3 URLs for previews if available
        logoUrl: existingTemplate.logo_url || designJson.logoUrl || '',
        backgroundImageUrl: existingTemplate.background_image_url || designJson.backgroundImageUrl || '',
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
      
      // Set preview states from existing S3 URLs
      if (existingTemplate.logo_url) {
        setLogoPreview(existingTemplate.logo_url);
        setLogoFileName('Current logo');
      }
      if (existingTemplate.background_image_url) {
        setBackgroundPreview(existingTemplate.background_image_url);
        setBackgroundFileName('Current background');
      }
      
      // Mark as initialized after loading existing template
      setIsInitializing(false);
    } else if (templateData) {
      const purpose = templateData.purpose || "welcome";
      const defaultTemplateId = PURPOSE_TEMPLATE_MAP[purpose];
      const template = TEMPLATES[defaultTemplateId];
      
      setSelectedTemplateId(defaultTemplateId);
      const initialData = {
        name: templateData.name || "",
        purpose: purpose,
        subject: templateData.subject || EMAIL_PURPOSES[purpose].defaultSubject,
        templateId: defaultTemplateId,
        ...template.getDefaultValues(purpose),
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
      
      // Mark as initialized
      setIsInitializing(false);
    } else if (isNewTemplate) {
      // For new templates, set original data to current form data
      setOriginalFormData(formData);
      setIsInitializing(false);
    }
  }, [existingTemplate, templateData, isNewTemplate]);

  // Compare current form data with original to detect changes
  useEffect(() => {
    if (!originalFormData) return;
    
    // Check if any field has changed
    const hasChanges = Object.keys(formData).some(key => {
      // Skip comparison for fields that aren't relevant to save state
      if (key === 'templateId') return false;
      return formData[key] !== originalFormData[key];
    });
    
    // Also check if new images have been uploaded or images have been deleted
    const hasImageChanges = logoUpload !== null || backgroundImageUpload !== null || 
                           deleteLogo || deleteBackgroundImage;
    
    setHasUnsavedChanges(hasChanges || hasImageChanges);
  }, [formData, originalFormData, logoUpload, backgroundImageUpload, deleteLogo, deleteBackgroundImage]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Image upload handlers
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      alert("Maximum file size is 1MB");
      return;
    }
    setLogoUpload(file);
    setLogoFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      updateField('logoUrl', reader.result); // For preview
    };
    reader.readAsDataURL(file);
    setDeleteLogo(false);
  };

  const handleLogoClear = () => {
    setLogoUpload(null);
    setLogoPreview(null);
    setLogoFileName("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setDeleteLogo(true);
    updateField('logoUrl', '');
  };

  const triggerLogoInput = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      alert("Maximum file size is 1MB");
      return;
    }
    setBackgroundImageUpload(file);
    setBackgroundFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundPreview(reader.result);
      updateField('backgroundImageUrl', reader.result); // For preview
    };
    reader.readAsDataURL(file);
    setDeleteBackgroundImage(false);
  };

  const handleBackgroundImageClear = () => {
    setBackgroundImageUpload(null);
    setBackgroundPreview(null);
    setBackgroundFileName("");
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = "";
    }
    setDeleteBackgroundImage(true);
    updateField('backgroundImageUrl', '');
  };

  const triggerBackgroundInput = () => {
    if (backgroundInputRef.current) {
      backgroundInputRef.current.click();
    }
  };

  // Generate preview HTML using the active template
  const previewHtml = useMemo(() => {
    return activeTemplate.generatePreviewHtml(formData);
  }, [formData, activeTemplate]);

  const handleSave = async () => {
    // For imported templates, only update metadata (no MJML/design_json generation)
    if (isImportedTemplate) {
      const saveData = {
        name: formData.name,
        purpose: formData.purpose,
        subject: formData.subject,
      };
      
      updateTemplate({
        templateId,
        data: saveData,
      }, {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          setOriginalFormData(formData);
          setToastMessage('Changes saved');
        },
        onError: (error) => {
          console.error('Failed to update template:', error);
          console.error('Response data:', error.response?.data);
          alert('Failed to save template. Please try again.');
        }
      });
      return;
    }
    
    // For internal templates, continue with normal save logic
    // Check if we have file uploads - use FormData if so
    const hasFileUploads = logoUpload || backgroundImageUpload || deleteLogo || deleteBackgroundImage;
    
    // For MJML generation, always use actual URLs (not empty strings)
    // This ensures the correct template structure is generated
    // The backend will update URLs after file upload
    const formDataForMjml = {
      ...formData,
      // Keep existing URLs for MJML structure - backend will replace them after upload
      logoUrl: deleteLogo ? '' : formData.logoUrl,
      backgroundImageUrl: deleteBackgroundImage ? '' : formData.backgroundImageUrl,
    };
    const mjmlContent = activeTemplate.generateMjml(formDataForMjml);
    
    // Prepare design_json without data URLs (we don't want to store base64 in DB)
    const designJsonForSave = {
      ...formData,
      templateId: selectedTemplateId,
      // Remove data URLs - the actual URLs will be from S3
      logoUrl: '',
      backgroundImageUrl: '',
    };
    
    let saveData;
    
    if (hasFileUploads) {
      // Use FormData for file uploads
      saveData = new FormData();
      saveData.append('name', formData.name);
      saveData.append('purpose', formData.purpose);
      saveData.append('subject', formData.subject);
      saveData.append('template_type', 'predefined');
      saveData.append('mjml_content', mjmlContent);
      saveData.append('design_json', JSON.stringify(designJsonForSave));
      
      if (logoUpload) {
        saveData.append('logo', logoUpload);
      }
      if (backgroundImageUpload) {
        saveData.append('background_image', backgroundImageUpload);
      }
      if (deleteLogo) {
        saveData.append('delete_logo', 'true');
      }
      if (deleteBackgroundImage) {
        saveData.append('delete_background_image', 'true');
      }
    } else {
      // Regular JSON payload
      saveData = {
        name: formData.name,
        purpose: formData.purpose,
        subject: formData.subject,
        template_type: 'predefined',
        mjml_content: mjmlContent,
        design_json: designJsonForSave,
      };
    }
    
    if (isNewTemplate) {
      if (!userProfile?.organisation_id) {
        alert('Unable to save: User profile not loaded.');
        return;
      }
      
      if (hasFileUploads) {
        saveData.append('organisation', userProfile.organisation_id);
      } else {
        saveData.organisation = userProfile.organisation_id;
      }
      
      createTemplate(saveData, {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          // Update originalFormData to reflect saved state
          setOriginalFormData(formData);
          setToastMessage('Template created successfully');
        },
        onError: (error) => {
          console.error('Failed to create template:', error);
          console.error('Response data:', error.response?.data);
          alert('Failed to save template. Please try again.');
        }
      });
    } else {
      updateTemplate({
        templateId,
        data: saveData,
      }, {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          // Update originalFormData to reflect saved state
          setOriginalFormData(formData);
          // Reset image upload states
          setLogoUpload(null);
          setBackgroundImageUpload(null);
          setDeleteLogo(false);
          setDeleteBackgroundImage(false);
          setToastMessage('Changes saved');
        },
        onError: (error) => {
          console.error('Failed to update template:', error);
          console.error('Response data:', error.response?.data);
          alert('Failed to save template. Please try again.');
        }
      });
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setBackAlertOpen(true);
    } else {
      navigate('/marketing/templates');
    }
  };
  
  // Confirm and navigate back
  const confirmBack = () => {
    setBackAlertOpen(false);
    navigate('/marketing/templates');
  };

  // Open the duplicate dialog, prefilling from the current template
  const handleOpenDuplicate = () => {
    setDuplicateForm({
      name: `Copy of ${formData.name}`,
      subject: formData.subject || "",
      purpose: formData.purpose || "welcome",
    });
    setDuplicateDialogOpen(true);
  };

  // Confirm duplication: clone on the backend, then navigate to the matching editor
  const handleConfirmDuplicate = async () => {
    if (isNewTemplate || !duplicateForm.name.trim()) return;
    try {
      const response = await duplicateTemplateAsync({
        templateId,
        data: {
          name: duplicateForm.name.trim(),
          subject: duplicateForm.subject.trim(),
          purpose: duplicateForm.purpose,
        },
      });
      const createdTemplate = response.data;
      setDuplicateDialogOpen(false);
      const editorPath = createdTemplate.template_type === 'scratch'
        ? `/marketing/templates/designer/${createdTemplate.template_id}`
        : `/marketing/templates/editor/${createdTemplate.template_id}`;
      navigate(editorPath, { state: { template: createdTemplate } });
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      alert(error.response?.data?.error || 'Failed to duplicate template. Please try again.');
    }
  };

  // Handle discarding changes
  const handleDiscard = () => {
    setDiscardAlertOpen(true);
  };
  
  // Confirm and execute discard
  const confirmDiscard = () => {
    // Reset form data to original state
    if (originalFormData) {
      setFormData(originalFormData);
      
      // Reset image upload states
      setLogoUpload(null);
      setBackgroundImageUpload(null);
      setDeleteLogo(false);
      setDeleteBackgroundImage(false);
      
      // Reset preview states to original
      if (existingTemplate?.logo_url) {
        setLogoPreview(existingTemplate.logo_url);
        setLogoFileName('Current logo');
      } else {
        setLogoPreview(null);
        setLogoFileName('');
      }
      
      if (existingTemplate?.background_image_url) {
        setBackgroundPreview(existingTemplate.background_image_url);
        setBackgroundFileName('Current background');
      } else {
        setBackgroundPreview(null);
        setBackgroundFileName('');
      }
      
      // Clear file inputs
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      if (backgroundInputRef.current) {
        backgroundInputRef.current.value = '';
      }
      
      setToastMessage('Changes discarded');
    }
    
    setDiscardAlertOpen(false);
  };

  // Handle sending test email
  const handleSendTestEmail = async () => {
    // Clear previous error
    setTestEmailError("");
    
    if (!testEmailAddress.trim()) {
      setTestEmailError('Please enter an email address');
      return;
    }

    // Validate email format
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(testEmailAddress)) {
      setTestEmailError('Please enter a valid email address');
      return;
    }

    // Check if template is saved
    if (isNewTemplate) {
      setTestEmailError('Please save the template before sending a test email');
      return;
    }

    setIsSendingTest(true);

    try {
      const senderDomainId = testSenderDomainId === "default" ? null : testSenderDomainId;
      const response = await sendTestEmail(templateId, testEmailAddress, senderDomainId);
      
      setToastMessage(`Test email sent successfully to ${testEmailAddress}`);
      setTestEmailDialogOpen(false);
      setTestEmailAddress('');
      setTestEmailError('');
      setTestSenderDomainId("default");
    } catch (error) {
      console.error('Error sending test email:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send test email';
      setTestEmailError(errorMessage);
    } finally {
      setIsSendingTest(false);
    }
  };

  // Get the field components from the active template
  const { ContentFields, BrandFields, ColorsFields, SocialFields } = activeTemplate;

  return (
  <>
    {/* Loading Progress Bar */}
    {isInitializing && (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    )}
    
    <Flex direction="column" p="5" gap="5" style={{ width: '100%', height: '100%', flex: '1 0 0'}}>
      {/* Navigation header with back button */}
      <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
        <Flex gap="4" style={{ alignItems: "center", flex: "1 0 0" }}>
          <IconButton size="2" variant="soft" color="gray"
            onClick={handleBack}>
            <ArrowLeftIcon />
          </IconButton>
          {/* Sidebar Header */}
          <Flex justify="between" align="center" gap="2">
            <Flex direction="column" gap="1">
              <Flex align="center" gap="2">
                <Text size="3" weight="bold">{formData.name}</Text>
              </Flex>
              <Text size="1" color="gray">Subject: {formData.subject}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex gap="2">
          <Button size="2" variant="soft" color="gray"
            onClick={handleOpenDuplicate}
            disabled={isNewTemplate || isDuplicating}>
            <CopyIcon width={16} height={16} />
            Duplicate template
          </Button>
          <Button size="2" variant="soft" color="amber"
            onClick={() => setTestEmailDialogOpen(true)}
            disabled={isNewTemplate}>
            <EnvelopeOpenIcon width={16} height={16} />
            Send test email
          </Button>
          <Tooltip content={`Switch email preview to ${view === "mobile" ? "desktop" : "mobile"} view`} side="bottom">
            <IconButton size="2" variant="soft" color="gray" onClick={() => setView(view === "mobile" ? "desktop" : "mobile")}>
              {view === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
            </IconButton>
          </Tooltip>
        </Flex>
      </Flex>
      
      {/* Test Email Dialog */}
      <Dialog.Root open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Send Test Email</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Enter the email address where you'd like to receive a test version of this template.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="medium">
                Email address
              </Text>
              <TextField.Root
                placeholder="example@email.com"
                value={testEmailAddress}
                onChange={(e) => {
                  setTestEmailAddress(e.target.value);
                  if (testEmailError) setTestEmailError(''); // Clear error on typing
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSendingTest) {
                    handleSendTestEmail();
                  }
                }}
              />
              {testEmailError && (
                <Text as="div" size="1" color="red" mt="1">
                  {testEmailError}
                </Text>
              )}
            </label>

            {/* Sender domain selector */}
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Send from</Text>
              <Select.Root value={testSenderDomainId} onValueChange={setTestSenderDomainId}>
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  <Select.Item value="default">Default (noreply@b-connect.co.uk)</Select.Item>
                  {(senderDomains || []).filter((d) => d.status === "verified" && d.link_status === "verified" && d.is_active).map((d) => (
                    <Select.Item key={d.sender_domain_id} value={d.sender_domain_id}>
                      {d.default_sender_name} &lt;{d.default_sender_email}&gt;
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Text size="1" color="gray">Choose which sender domain to use for this test email.</Text>
            </Flex>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={isSendingTest}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button 
              onClick={handleSendTestEmail}
              disabled={isSendingTest || !testEmailAddress.trim()}
              loading={isSendingTest}
            >
              Send
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      
      {/* Duplicate Template Dialog */}
      <Dialog.Root open={duplicateDialogOpen} onOpenChange={(open) => { if (!open && !isDuplicating) setDuplicateDialogOpen(false); }}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Copy email template</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="4">
            Create a duplicate of "{formData.name}". The copy starts as an exact clone that you can edit independently.
          </Dialog.Description>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Name</Text>
              <TextField.Root
                placeholder="Template Name"
                value={duplicateForm.name}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })}
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Subject</Text>
              <TextField.Root
                placeholder="Email's subject line"
                value={duplicateForm.subject}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, subject: e.target.value })}
              />
            </Flex>
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={isDuplicating}>Cancel</Button>
            </Dialog.Close>
            <Button
              onClick={handleConfirmDuplicate}
              disabled={!duplicateForm.name.trim() || !duplicateForm.subject.trim() || isDuplicating}
              loading={isDuplicating}
            >
              Create copy
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Discard Changes Alert Dialog */}
      <AlertDialog.Root open={discardAlertOpen} onOpenChange={setDiscardAlertOpen}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Discard changes?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to discard all unsaved changes? This action cannot be undone.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={confirmDiscard}>
                Discard changes
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
      
      {/* Back Navigation Alert Dialog */}
      <AlertDialog.Root open={backAlertOpen} onOpenChange={setBackAlertOpen}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Leave without saving?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={confirmBack}>
                Leave without saving
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
      
      {/* Main Content */}
      <Flex gap="5" style={{ flex: '1 0 0', overflow: 'hidden' }}>
        {/* Left Panel - Show info panel for imported templates, editing sidebar for internal templates */}
        {isImportedTemplate ? (
          // Imported template - show info panel
          <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
            <Flex width="420px" direction="column"
              style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0",
              borderRadius: "var(--radius-4)", height: "100%", border: "1px solid var(--gray-a6)",
              backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
              <Flex direction="column" style={{ alignSelf: "stretch", flex: "1 0 0", overflow: "hidden" }}>
                <Flex direction="column" gap="4" style={{ alignSelf: "stretch", overflow: "auto", flex: "1 0 0" }}>
                  <Flex direction="column" gap="1" px="4" pt="4">
                    {/* <Text size="2" weight="medium">Imported Template</Text> */}
                    <Text size="1" color="gray" weight="medium">
                      This template has been imported from an external source. You can edit the metadata below.
                    </Text>
                  </Flex>
                  
                  <Separator size="4" style={{ minHeight: '1px' }} />
                  
                  <Flex direction="column" gap="3" px="4">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Template name</Text>
                      <TextField.Root 
                        placeholder="e.g., Welcome Email" 
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        style={{ alignSelf: 'stretch'}}
                      />
                    </Flex>
                    
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Subject line</Text>
                      <TextField.Root 
                        placeholder="Welcome to {{business_name}}!" 
                        value={formData.subject}
                        onChange={(e) => updateField('subject', e.target.value)}
                        style={{ alignSelf: 'stretch'}}
                      />
                    </Flex>
                    
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Purpose</Text>
                      <Select.Root value={formData.purpose} onValueChange={(val) => updateField('purpose', val)}>
                        <Select.Trigger style={{ width: '100%' }} />
                        <Select.Content>
                          <Select.Item value="welcome">Welcome Email</Select.Item>
                          <Select.Item value="newsletter">Newsletter</Select.Item>
                          <Select.Item value="review" disabled>
                            <Flex gap="1" align="center">
                              Review Request
                              <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                            </Flex>
                          </Select.Item>
                          <Select.Item value="promotion" disabled>
                            <Flex gap="1" align="center">
                              Promotional Email
                              <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                            </Flex>
                          </Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </Flex>
                    
                  <Separator size="4" style={{ minHeight: '1px' }} />
                    
                  <Flex justify="between" gap="2" px="4">
                    <Text size="2" weight="medium">Import source</Text>
                    <Badge variant="soft" style={{ width: 'fit-content' }}>
                      {existingTemplate?.template_source === 'canva' ? 'Canva' : existingTemplate?.template_source}
                    </Badge>
                  </Flex>
                    
                  {existingTemplate?.imported_images && Object.keys(existingTemplate.imported_images).length > 0 && (
                    <Flex justify="between" gap="2" align="center" px="4">
                      <Text size="2" weight="medium">Imported assets</Text>
                      <Text size="1" color="gray">
                        {Object.keys(existingTemplate.imported_images).length} image(s) imported
                      </Text>
                    </Flex>
                  )}
                </Flex>
                
                
                {/* Save changes */}
                <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                <Flex p="4" gap="2" style={{ alignSelf: "stretch" }}>
                  <Button
                    variant="soft"
                    color="red"
                    onClick={handleDiscard} 
                    disabled={!hasUnsavedChanges || isUpdating}
                    style={{ flex: 1 }}
                  >
                    Discard changes
                  </Button>
                  <Button
                    variant="solid"
                    onClick={handleSave} 
                    disabled={isUpdating || !formData.name.trim() || !formData.subject.trim() || !hasUnsavedChanges}
                    loading={isUpdating}
                    style={{ flex: 1 }}
                  >
                    Save changes
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        ) : (
          // Internal template - show editing sidebar
        <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
          <Flex width="420px" direction="column"
            style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0",
            borderRadius: "var(--radius-4)", height: "100%", border: "1px solid var(--gray-a6)",
            backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
            <Flex direction="column" style={{ alignSelf: "stretch", overflow: "scroll", flex: "1 0 0" }}>
              {/* Tabs */}
              <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Flex direction="column" style={{ position: "sticky", top: "0", zIndex: "2", 
                  backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
                  <Tabs.List style={{
                    padding: "var(--space-2) var(--space-4) 0px var(--space-4)",
                    borderTopLeftRadius: "var(--radius-4)",
                    borderTopRightRadius: "var(--radius-4)",
                  }}>
                    <Tabs.Trigger value="general">General</Tabs.Trigger>
                    <Tabs.Trigger value="brand">Brand</Tabs.Trigger>
                    <Tabs.Trigger value="colors">Pallete</Tabs.Trigger>
                    <Tabs.Trigger value="content">Content</Tabs.Trigger>
                    <Tabs.Trigger value="social">Socials</Tabs.Trigger>
                  </Tabs.List>
                </Flex>
                {/* General Tab */}
                <Tabs.Content value="general" key="general-tab">
                  <Flex direction="column" gap="4">
                    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Text size="1" weight="regular" color="gray">
                        Configure the basic settings for your email template.
                      </Text>
                    </Flex>
                    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                    {/* Template Settings */}
                    <Flex direction="column" gap="3">
                      <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="2" weight="medium">Template name</Text>
                        <TextField.Root 
                          placeholder="e.g., Welcome Email" 
                          value={formData.name}
                          onChange={(e) => updateField('name', e.target.value)}
                          style={{ alignSelf: 'stretch'}}
                        />
                      </Flex>
                      <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="2" weight="medium">Subject line</Text>
                        <TextField.Root 
                          placeholder="Welcome to {{business_name}}!" 
                          value={formData.subject}
                          onChange={(e) => updateField('subject', e.target.value)}
                          style={{ alignSelf: 'stretch'}}
                        />
                      </Flex>
                      <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="2" weight="medium">Purpose</Text>
                        <Select.Root value={formData.purpose} onValueChange={handlePurposeChange}>
                          <Select.Trigger style={{ width: '100%' }} />
                          <Select.Content>
                            <Select.Item value="welcome">Welcome Email</Select.Item>
                            <Select.Item value="newsletter">Newsletter</Select.Item>
                            <Select.Item value="review">Review Request</Select.Item>
                            <Select.Item value="promotion" disabled>
                              <Flex gap="1" align="center">
                                Promotional Email
                                <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                              </Flex>
                            </Select.Item>
                          </Select.Content>
                        </Select.Root>
                        <Text size="1" color="gray" mt="1">Template style is automatically selected based on purpose.</Text>
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}}/>
                  </Flex>
                </Tabs.Content>
                {/* Content Tab */}
                <Tabs.Content value="content" key="content-tab">
                  <Flex direction="column" gap="4">
                    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Text size="1" weight="regular" color="gray">
                        Customize the content and structure of your email template here.
                      </Text>
                    </Flex>
                    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                    
                    {/* Template-specific content fields */}
                    <ContentFields formData={formData} updateField={updateField} />
                  </Flex>
                </Tabs.Content>
                
                {/* Brand Tab */}
                <Tabs.Content value="brand" key="brand-tab">
                  <BrandFields 
                    formData={formData} 
                    updateField={updateField}
                    logoPreview={logoPreview}
                    logoFileName={logoFileName}
                    logoInputRef={logoInputRef}
                    handleLogoUpload={handleLogoUpload}
                    handleLogoClear={handleLogoClear}
                    triggerLogoInput={triggerLogoInput}
                    backgroundPreview={backgroundPreview}
                    backgroundFileName={backgroundFileName}
                    backgroundInputRef={backgroundInputRef}
                    handleBackgroundImageUpload={handleBackgroundImageUpload}
                    handleBackgroundImageClear={handleBackgroundImageClear}
                    triggerBackgroundInput={triggerBackgroundInput}
                  />
                </Tabs.Content>

                {/* Colors Tab */}
                <Tabs.Content value="colors" key="colors-tab">
                  <ColorsFields formData={formData} updateField={updateField} />
                </Tabs.Content>

                {/* Social Tab */}
                <Tabs.Content value="social" key="social-tab">
                  <SocialFields formData={formData} updateField={updateField} />
                </Tabs.Content>
              </Tabs.Root>
            </Flex>

            {/* Save changes */}
            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
            <Flex p="4" gap="2" style={{ alignSelf: "stretch" }}>
              <Button
                variant="soft"
                color="red"
                onClick={handleDiscard} 
                disabled={!hasUnsavedChanges || isUpdating || isCreating}
                style={{ flex: 1 }}
              >
                Discard changes
              </Button>
              <Button
                variant="solid"
                onClick={handleSave} 
                disabled={isUpdating || isCreating || !formData.name.trim() || !formData.subject.trim() || !hasUnsavedChanges}
                loading={isUpdating || isCreating}
                style={{ flex: 1 }}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </Flex>
        )}
        
        {/* Right Panel - Preview */}
        <Flex direction="column"
          style={{ alignItems: "stretch", alignSelf: "stretch", flex: "1 0 0", borderRadius: "var(--radius-4)",
            overflow: "hidden", border: "1px solid var(--gray-a6)",
            backgroundColor: "var(--gray-a3)",
          }}>
          {/* Scrollable Preview Content */}
          <Box style={{ padding: 40, overflow: "auto", flex: "1 1 0", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            {isImportedTemplate && existingTemplate?.html_content ? (
              // Show imported HTML content
              <Box 
                style={{ 
                  background: 'white', 
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  width: view === 'mobile' ? 375 : 600,
                  maxWidth: '100%',
                  margin: '0 auto',
                  transition: 'width 0.3s ease'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: existingTemplate.html_content }} />
              </Box>
            ) : (
              // Show generated preview from design_json
              <Box 
                style={{ 
                  background: 'white', 
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  width: view === 'mobile' ? 375 : 600,
                  maxWidth: '100%',
                  margin: '0 auto',
                  borderRadius: selectedTemplateId === 'thank-you-review' ? '24px' : '0',
                  transition: 'width 0.3s ease'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </Box>
            )}
          </Box>
        </Flex>
      </Flex>
    </Flex>
    <ToastAlert/>
  </>
  );
}
